/**
 * Date-range selector for the Tenants pages.
 *
 * Three shortcut buttons (1d / 7d / 30d) plus a custom mode that exposes
 * two `<input type="date">` controls. State is owned by the caller so all
 * three drill-down levels can share the same window across navigation.
 */
import { clsx } from 'clsx';
import type { CostPeriod, CostWindowParams } from '@/api/tenants';

const SHORTCUTS: { value: Exclude<CostPeriod, 'custom'>; label: string }[] = [
  { value: '1d', label: '1 day' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

interface Props {
  value: CostWindowParams;
  onChange: (next: CostWindowParams) => void;
}

export default function CostWindowSelector({ value, onChange }: Props) {
  const isCustom = value.period === 'custom';

  function selectShortcut(period: Exclude<CostPeriod, 'custom'>) {
    onChange({ period });
  }

  function selectCustom() {
    if (isCustom) return;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    onChange({
      period: 'custom',
      start: start.toISOString(),
      end: end.toISOString(),
    });
  }

  function updateBound(which: 'start' | 'end', dateValue: string) {
    if (!dateValue) return;
    const iso = new Date(dateValue + 'T00:00:00Z').toISOString();
    onChange({ ...value, period: 'custom', [which]: iso });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Window:</span>
      {SHORTCUTS.map((s) => (
        <button
          key={s.value}
          type="button"
          onClick={() => selectShortcut(s.value)}
          className={clsx(
            'rounded-md border px-3 py-1.5 text-sm transition-colors',
            value.period === s.value
              ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
          )}
        >
          {s.label}
        </button>
      ))}
      <button
        type="button"
        onClick={selectCustom}
        className={clsx(
          'rounded-md border px-3 py-1.5 text-sm transition-colors',
          isCustom
            ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
        )}
      >
        Custom
      </button>
      {isCustom && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={value.start ? value.start.slice(0, 10) : ''}
            onChange={(e) => updateBound('start', e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
          <span className="text-sm text-slate-500">→</span>
          <input
            type="date"
            value={value.end ? value.end.slice(0, 10) : ''}
            onChange={(e) => updateBound('end', e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
        </div>
      )}
    </div>
  );
}
